import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Card,
  CardBody,
  Spinner,
  Button,
} from "@heroui/react";
import {
  FileText,
  CalendarBlank,
  FolderSimple,
  FolderOpen,
  ArrowSquareOut,
  Gear,
  Plus,
} from "@phosphor-icons/react";
import { CompanyDocumentLabel } from "shared/types/company-documents";
import { UniversalTableSearch } from "shared/components/UniversalTable/components/UniversalTableSearch";
import type { UniversalFilterSection } from "shared/components/UniversalTable/components/UniversalTableSearch";
import CompanyDocumentUploadModal from "./CompanyDocumentUploadModal";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";

interface CompanyDocumentsTabProps {
  company: any;
  refreshCompany?: () => void;
  onFileUploaded?: (fileLogData: any) => void;
  hasEditRights?: boolean;
}

interface CompanyDocument {
  id: number;
  label?: string[];
  summary?: string;
  documentDate?: string;
  sharePointFileUrl?: string;
  folderName?: string | null;
  folderUrl?: string | null;
  relations?: { id: number; name: string; type?: string }[];
}

const CompanyDocumentsTab = ({
  company,
  refreshCompany,
  hasEditRights = true,
}: CompanyDocumentsTabProps) => {
  const { session } = useSession();
  const [labels, setLabels] = useState<CompanyDocumentLabel[]>([]);
  const [documents, setDocuments] = useState<CompanyDocument[]>([]);
  const [isCreatingStructure, setIsCreatingStructure] = useState(false);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({
    label: [],
  });

  const hasFolderStructure =
    !!company?.companyFolderStructure &&
    Object.keys(company.companyFolderStructure).length > 0;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    fetchLabels();
  }, []);

  const selectedLabelFilter = activeFilters.label?.[0] || "";

  useEffect(() => {
    if (company?.id) {
      fetchDocuments();
    }
  }, [company?.id, debouncedSearch, selectedLabelFilter]);

  const fetchLabels = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/company-documents/labels`,
        { method: "GET" },
        session,
      );
      if (!response.ok)
        throw new Error(`Failed to fetch labels: ${response.status}`);
      const data = await response.json();
      setLabels(data);
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error fetching company document labels:", error);
    }
  };

  const fetchDocuments = async () => {
    if (!company?.id) return;
    setIsLoadingDocs(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (selectedLabelFilter) params.append("labelId", selectedLabelFilter);

      const queryString = params.toString();
      const url = `${API_URL}/company-documents/companies/${company.id}/documents${queryString ? `?${queryString}` : ""}`;

      const response = await fetchWithAuth(url, { method: "GET" }, session);
      if (!response.ok)
        throw new Error(`Failed to fetch documents: ${response.status}`);
      const data = await response.json();
      setDocuments(data);
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error fetching company documents:", error);
      toast.error("Fout bij ophalen van documenten");
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const handleEnsureFolderStructure = async () => {
    if (!company?.id) return;
    setIsCreatingStructure(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/company-documents/companies/${company.id}/ensure-folder-structure`,
        { method: "POST" },
        session,
      );
      if (!response.ok) {
        let backendMessage = "";
        try {
          const errorBody = await response.json();
          backendMessage = Array.isArray(errorBody?.message)
            ? errorBody.message.join(", ")
            : errorBody?.message || "";
        } catch {
          backendMessage = await response.text();
        }
        toast.error(
          backendMessage || "Fout bij aanmaken van mappenstructuur",
        );
        throw new Error(
          `Failed to create folder structure: ${response.status} — ${backendMessage}`,
        );
      }
      toast.success("Mappenstructuur succesvol aangemaakt");
      refreshCompany && refreshCompany();
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error creating folder structure:", error);
    } finally {
      setIsCreatingStructure(false);
    }
  };

  const handleUploadComplete = async () => {
    await fetchDocuments();
    refreshCompany && refreshCompany();
  };

  // ── Relations editor ─────────────────────────────────────────────────────
  const [relationsEditorDoc, setRelationsEditorDoc] =
    useState<CompanyDocument | null>(null);

  const handleRelationsConfirm = async (picked: any) => {
    if (!relationsEditorDoc) return;
    const existing = relationsEditorDoc.relations || [];
    let nextRelations: { id: number; name: string; type?: string }[];
    if (picked === null || picked === undefined) {
      nextRelations = [];
    } else if (Array.isArray(picked)) {
      nextRelations = picked;
    } else {
      const merged = [...existing, picked];
      nextRelations = merged.filter(
        (r, i, arr) =>
          arr.findIndex((x) => x.id === r.id && x.type === r.type) === i,
      );
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/update-file-log/${relationsEditorDoc.id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ relations: nextRelations }),
        },
        session,
      );
      if (!response.ok) {
        throw new Error(`Failed to update relations: ${response.status}`);
      }
      toast.success("Relaties bijgewerkt");
      setRelationsEditorDoc(null);
      await fetchDocuments();
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error updating relations:", error);
      toast.error("Fout bij bijwerken van relaties");
    }
  };

  const filterSections: UniversalFilterSection[] = useMemo(
    () => [
      {
        key: "label",
        label: "Label",
        color: "moss" as const,
        singleSelect: true,
        options: labels.map((l) => ({ key: l.id.toString(), label: l.label })),
      },
    ],
    [labels],
  );

  const handleFilterChange = (key: string, values: string[]) => {
    setActiveFilters((prev) => ({ ...prev, [key]: values }));
  };

  const getDocumentLabel = (doc: CompanyDocument): string => {
    if (Array.isArray(doc.label) && doc.label.length > 0) return doc.label[0];
    if (typeof doc.label === "string") return doc.label;
    return "Geen label";
  };

  // ── No folder structure yet ───────────────────────────────────────────────

  if (!hasFolderStructure) {
    return (
      <Card shadow="none" className="border border-neutral-200">
        <CardBody className="flex flex-col items-center gap-4 py-10 text-center">
          <div className="bg-moss-primary/10 rounded-full p-4">
            <FolderSimple
              className="text-moss-primary h-8 w-8"
              weight="duotone"
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-neutral-primary font-semibold">
              Geen mappenstructuur aangemaakt
            </p>
            <p className="text-neutral-secondary-mid text-sm">
              Er is nog geen mappenstructuur aangemaakt voor deze vennootschap.
              Definieer eerst een mappenstructuur in de Instellingen.
            </p>
          </div>
          {hasEditRights && (
            <Button
              className="bg-moss-primary text-white"
              onClick={handleEnsureFolderStructure}
              isDisabled={isCreatingStructure}
              startContent={
                isCreatingStructure ? (
                  <Spinner size="sm" color="white" />
                ) : (
                  <FolderSimple size={16} />
                )
              }
            >
              {isCreatingStructure
                ? "Bezig met aanmaken..."
                : "Mappenstructuur aanmaken"}
            </Button>
          )}
        </CardBody>
      </Card>
    );
  }

  // ── Folder structure exists ───────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="text-moss-primary h-5 w-5" weight="duotone" />
          <h3 className="text-neutral-primary text-base font-semibold">
            Vennootschapsdocumenten
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <RouterLink
            to="/settings#vennootschapsmappen"
            className="text-neutral-secondary-mid hover:text-moss-primary hover:bg-moss-primary/5 inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-1.5 text-sm font-medium transition-colors"
          >
            <Gear className="h-4 w-4" weight="duotone" />
            Beheer labels
          </RouterLink>
          {company?.sharePointFolderUrl && (
            <a
              href={company.sharePointFolderUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-moss-primary hover:bg-moss-primary/5 inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-1.5 text-sm font-medium transition-colors"
            >
              <FolderOpen className="h-4 w-4" weight="duotone" />
              Open in SharePoint
            </a>
          )}
          {hasEditRights && (
            <Button
              className="bg-moss-primary text-white"
              onClick={() => setIsUploadModalOpen(true)}
              startContent={<FileText size={16} />}
            >
              Document toevoegen
            </Button>
          )}
        </div>
      </div>

      <UniversalTableSearch
        rows={documents}
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder="Zoek op samenvatting of label..."
        filterSections={filterSections}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
        showSpacer={false}
      />

      <Card shadow="none" className="border border-gray-200">
        <CardBody className="p-0">
          {isLoadingDocs ? (
            <div className="flex items-center justify-center py-10">
              <Spinner size="md" />
            </div>
          ) : (
            <Table
              removeWrapper
              classNames={{
                th: "bg-gray-50 text-xs font-semibold",
                td: "text-sm",
              }}
            >
              <TableHeader>
                <TableColumn>DATUM</TableColumn>
                <TableColumn>LABEL</TableColumn>
                <TableColumn>MAP</TableColumn>
                <TableColumn>SAMENVATTING</TableColumn>
                <TableColumn>RELATIES</TableColumn>
                <TableColumn>SHAREPOINT</TableColumn>
              </TableHeader>
              <TableBody emptyContent="Geen documenten gevonden">
                {documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <CalendarBlank
                          className="h-4 w-4 text-gray-400"
                          weight="duotone"
                        />
                        <span className="text-neutral-secondary-mid">
                          {doc.documentDate || "Datum niet ingevuld"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText
                          className="text-moss-primary h-4 w-4"
                          weight="duotone"
                        />
                        <span className="text-neutral-primary font-medium">
                          {getDocumentLabel(doc)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {doc.folderName ? (
                        doc.folderUrl ? (
                          <a
                            href={doc.folderUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-moss-primary hover:text-moss-primary/80 inline-flex items-center gap-1 transition-colors"
                          >
                            <FolderSimple className="h-4 w-4" weight="duotone" />
                            <span>{doc.folderName}</span>
                          </a>
                        ) : (
                          <div className="text-neutral-secondary-mid inline-flex items-center gap-1">
                            <FolderSimple className="h-4 w-4" weight="duotone" />
                            <span>{doc.folderName}</span>
                          </div>
                        )
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-neutral-secondary-mid">
                        {doc.summary || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => setRelationsEditorDoc(doc)}
                        className="hover:bg-neutral-secondary-light/30 flex w-full flex-wrap items-center gap-1 rounded px-1 py-1 text-left transition-colors"
                        title="Relaties bewerken"
                        disabled={!hasEditRights}
                      >
                        {doc.relations && doc.relations.length > 0 ? (
                          doc.relations.map((rel) => (
                            <span
                              key={`${rel.type ?? "rel"}-${rel.id}`}
                              className="text-neutral-primary inline-flex items-center rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-xs font-medium"
                            >
                              {rel.name}
                            </span>
                          ))
                        ) : hasEditRights ? (
                          <span className="text-moss-primary inline-flex items-center gap-1 text-xs font-medium">
                            <Plus size={12} weight="bold" />
                            Relatie toevoegen
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </button>
                    </TableCell>
                    <TableCell>
                      {doc.sharePointFileUrl ? (
                        <a
                          href={doc.sharePointFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-moss-primary hover:text-moss-primary/80 flex items-center gap-1 transition-colors"
                        >
                          <ArrowSquareOut className="h-4 w-4" />
                          <span className="text-sm">Bekijk</span>
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400">
                          Geen document
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Company document upload modal */}
      <CompanyDocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadComplete={handleUploadComplete}
        companyId={company.id}
        labels={labels}
        onLabelsRefresh={fetchLabels}
      />

      {/* Relations picker for any document row */}
      <UniversalPickerModal
        isOpen={!!relationsEditorDoc}
        mode="resource"
        title={
          relationsEditorDoc
            ? `Relaties beheren voor bestand ${relationsEditorDoc.id}`
            : "Relaties beheren"
        }
        onClose={() => setRelationsEditorDoc(null)}
        onConfirm={handleRelationsConfirm}
        config={{
          defaultValue: relationsEditorDoc?.relations || [],
          allowRemoval: true,
          searchPlaceholder: "Zoek contact of vennootschap...",
        }}
      />
    </div>
  );
};

export default CompanyDocumentsTab;
